import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from './GalleryPage.module.css';
import FullscreenImageModal from '../components/FullscreenImageModal';
import { digitalOceanSpacesService } from '../../services/digitalOceanSpacesService';
import { Image } from '../components/ImagePreviewModal';
import Header from '../components/Header';
import Footer from '../components/Footer';

interface MediaGrid {
  items: Image[];
  hasMore: boolean;
  nextToken?: string;
}

const GalleryPage: React.FC = () => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedMedia, setSelectedMedia] = useState<Image | null>(null);
  const [mediaGrid, setMediaGrid] = useState<MediaGrid>({
    items: [],
    hasMore: true,
    nextToken: undefined
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const galleryRef = useRef<HTMLDivElement>(null);
  const layoutTimerRef = useRef<number | null>(null);
  const [isDesktop, setIsDesktop] = useState<boolean>(window.innerWidth > 1024);

  const ITEMS_PER_PAGE = 20;

  // Track screen size for responsive image loading
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Masonry layout function - simplified and more reliable
  const applyMasonryLayout = useCallback(() => {
    if (!galleryRef.current) return;

    const container = galleryRef.current;
    const items = container.querySelectorAll(`.${styles.mediaItem}`);

    // Responsive gap based on screen size
    const gap = window.innerWidth <= 480 ? 16 : window.innerWidth <= 768 ? 24 : 32;
    const containerWidth = container.offsetWidth;
    const itemWidth = (containerWidth - gap) / 2;

    // Heights of the two columns
    let leftHeight = 0;
    let rightHeight = 0;

    // Batch read all heights first (read phase)
    const itemHeights: number[] = [];
    items.forEach((item) => {
      itemHeights.push((item as HTMLElement).offsetHeight);
    });

    // Then batch write all positions (write phase - prevents layout thrashing)
    items.forEach((item, index) => {
      const element = item as HTMLElement;
      const itemHeight = itemHeights[index];

      // Determine which column to place the item in (shortest column)
      const useLeftColumn = leftHeight <= rightHeight;

      // Set the width and position
      element.style.width = `${itemWidth}px`;
      element.style.position = 'absolute';

      if (useLeftColumn) {
        element.style.left = '0px';
        element.style.top = `${leftHeight}px`;
        leftHeight += itemHeight + gap;
      } else {
        element.style.left = `${itemWidth + gap}px`;
        element.style.top = `${rightHeight}px`;
        rightHeight += itemHeight + gap;
      }
    });

    // Set container height to the tallest column
    const maxHeight = Math.max(leftHeight, rightHeight);
    container.style.height = `${maxHeight - gap}px`;
  }, []);

  // Apply masonry layout after images load
  useEffect(() => {
    if (mediaGrid.items.length > 0) {
      // Small delay to ensure images have dimensions
      const timer = setTimeout(() => {
        applyMasonryLayout();
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [mediaGrid.items, applyMasonryLayout]);

  // Re-apply masonry on window resize (with minimal debounce)
  useEffect(() => {
    let resizeTimer: number | null = null;

    const handleResize = () => {
      if (resizeTimer) {
        clearTimeout(resizeTimer);
      }
      resizeTimer = window.setTimeout(() => {
        applyMasonryLayout();
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimer) {
        clearTimeout(resizeTimer);
      }
    };
  }, [applyMasonryLayout]);

  // Handle individual image loads - throttled to batch updates
  const handleImageLoad = useCallback(() => {
    if (layoutTimerRef.current) {
      clearTimeout(layoutTimerRef.current);
    }

    layoutTimerRef.current = window.setTimeout(() => {
      applyMasonryLayout();
    }, 50);
  }, [applyMasonryLayout]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (layoutTimerRef.current) {
        clearTimeout(layoutTimerRef.current);
      }
    };
  }, []);

  // Fetch media from Digital Ocean Spaces with server-side pagination
  const fetchMedia = useCallback(async (append: boolean = false, continuationToken?: string) => {
    try {
      if (!append) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);

      // Fetch images with pagination support
      const response = await digitalOceanSpacesService.listGalleryFiles(ITEMS_PER_PAGE, continuationToken);

      if (response.success && response.files) {
        setMediaGrid(prev => {
          const currentItems = append ? prev.items : [];
          const updatedItems = [...currentItems, ...response.files];

          return {
            items: updatedItems,
            hasMore: response.hasMore,
            nextToken: response.nextToken
          };
        });
      } else {
        setError(response.error || 'Failed to load gallery images');
      }
    } catch (err) {
      console.error('Error fetching media:', err);
      setError('Error loading gallery');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchMedia(false);
  }, [fetchMedia]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && mediaGrid.hasMore && !loadingMore && mediaGrid.items.length > 0) {
            fetchMedia(true, mediaGrid.nextToken);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '200px' // Preload before reaching bottom
      }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [mediaGrid.hasMore, loadingMore, mediaGrid.nextToken, mediaGrid.items.length, fetchMedia]);

  const handleMediaClick = (media: Image): void => {
    setSelectedMedia(media);
    setModalOpen(true);
  };

  return (
    <div className={styles.galleryPage}>
      <Header activeSection="gallery" activeTheme="light" />

      <main className={styles.mainContent}>
        <div className={styles.header}>
          <h1 className={styles.title}>Our Complete Gallery</h1>
          <p className={styles.subtitle}>
            Browse through our collection of completed tiling projects
          </p>
        </div>

        {loading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <p>Loading gallery...</p>
          </div>
        ) : error ? (
          <div className={styles.errorContainer}>
            <p>Error: {error}</p>
          </div>
        ) : mediaGrid.items.length === 0 ? (
          <div className={styles.emptyContainer}>
            <p>No gallery images found.</p>
          </div>
        ) : (
          <>
            <div ref={galleryRef} className={styles.galleryGrid}>
              {mediaGrid.items.map((media, index) => (
                <div key={media.name} className={styles.mediaItem}>
                  <div
                    className={styles.mediaButton}
                    onClick={() => handleMediaClick(media)}
                  >
                    {media.mediaType === 'video' ? (
                      <></>
                    ) : (
                      <img
                        src={isDesktop ? media.mediumPath : media.thumbPath}
                        alt={media.name}
                        className={styles.mediaImage}
                        loading={index < 10 ? 'eager' : 'lazy'}
                        decoding="async"
                        onLoad={handleImageLoad}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Loading more indicator */}
            <div ref={loadMoreRef} className={styles.loadMoreTrigger}>
              {loadingMore && (
                <div className={styles.loadingMore}>
                  <div className={styles.spinner}></div>
                  <p>Loading more images...</p>
                </div>
              )}
              {!mediaGrid.hasMore && mediaGrid.items.length > 0 && (
                <div className={styles.endMessage}>
                  <p>You've reached the end of our gallery!</p>
                </div>
              )}
            </div>
          </>
        )}
      </main>

      <Footer />

      {modalOpen && selectedMedia && (
        <FullscreenImageModal
          image={selectedMedia}
          isOpen={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setSelectedMedia(null);
          }}
        />
      )}
    </div>
  );
};

export default GalleryPage;
